import { AnimatePresence, motion } from 'motion/react'
import styled from 'styled-components'

import { Tooltip } from '@blade/ui-kit'

import { PLATFORM_LABEL_MAP } from '../../../model/platform'
import { PlatformIcon } from '../../platform-icon'

import type { Platform } from '@root/entities/platform/model'

type Props = {
  expanded: boolean
  platform: Platform.Platform
  onClick: () => void
  variant: 'filled' | 'outlined'
  withSeparator?: boolean
}

export const PlatformSelectorItem: React.FC<Props> = ({
  expanded,
  platform,
  onClick,
  variant,
  withSeparator = false,
}: Props) => (
  <Root onClick={onClick}>
    <Tooltip
      side="right"
      sideOffset={15}
      content={expanded ? undefined : PLATFORM_LABEL_MAP[platform]}
    >
      <Group>
        <IconSlot>
          <AnimatePresence initial={false}>
            <motion.div
              key={variant}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15, ease: 'easeInOut' }}
              style={{
                position: 'absolute',
                inset: 0,
                display: 'grid',
                placeContent: 'center',
                willChange: 'opacity, scale',
              }}
            >
              <PlatformIcon
                platform={platform}
                size={20}
                frameSize={36}
                variant={variant}
              />
            </motion.div>
          </AnimatePresence>
        </IconSlot>
        <Label aria-expanded={expanded}>{PLATFORM_LABEL_MAP[platform]}</Label>
      </Group>
    </Tooltip>
    {withSeparator ? <Separator /> : undefined}
  </Root>
)

const Root = styled.div`
  display: grid;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  cursor: pointer;
`

const Separator = styled.div`
  border-bottom: 1px solid var(--backgroundA-2);
  margin-inline: 5px;
`

const Label = styled.span`
  color: var(--text-11);

  text-align: center;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: -0.14px;
  padding-inline-start: 12px;

  transition: all 0.15s ease-in-out;
  opacity: 0;

  &[aria-expanded='true'] {
    opacity: 1;
  }
  ${Root}:hover & {
    color: var(--text-12);
  }
`

const IconSlot = styled.div`
  position: relative;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
`

const Group = styled.div`
  display: grid;
  align-items: center;
  justify-content: start;
  justify-items: start;
  grid-template-columns: auto 0;

  &[aria-expanded='true'] {
    grid-template-columns: auto auto;
  }
`
